import { MapBrowserEvent } from "ol";
import { EventsKey } from "ol/events";
import BaseEvent from "ol/events/Event";
import { Select } from "ol/interaction";
import { SelectEvent } from "ol/interaction/Select";
import olMap from 'ol/Map';
import { Types } from "ol/MapBrowserEventType";
import { unByKey } from "ol/Observable";



/**
 * Class Help:
 * 
 * ### Events on a Map
 * Events on a map allow developers to interact with user actions such as:
 * - **Single Click Events (singleclick)**: Triggered when a user clicks once on the map.
 * - **Click Events (click)**: Triggered when a user clicks on the map (may include double-clicks).
 * - **Double Click Events (dblclick)**: Triggered when a user double-clicks on the map.
 * - **Pointer Drag Events (pointerdrag)**: Triggered when a user drags an element on the map.
 * - **Pointer Move Events (pointermove)**: Triggered when a user moves their cursor over the map.
 *
 * ### Select Events
 * Select events occur when a user interacts with selectable features on the map. These include:
 * - Selecting a point, line, or polygon.
 * - Deselecting a previously selected feature.
 * - Triggering actions based on selection, such as highlighting or fetching additional information.
 */


class SingleEntryMap<K, V> extends Map<K, V> {
    set(key: K, value: V): this {
        // Clear the map if there's an existing entry, ensuring only one entry is present.
        if (this.size >= 1) {
            EventListenersOnMap.RemoveEventListener(this.keys().next().value, true);
        }
        return super.set(key, value);
    }
}

/**
 * EventListenersOnMap is a utility class for managing event listeners on an OpenLayers map instance.
 * It supports both regular event listeners (e.g., click, hover) and select interactions for features.
 */
export class EventListenersOnMap {

    /** Stores exclusive event listeners, ensuring only one exclusive event is active at a time. */
    private static readonly exclusiveEventListenersMap: SingleEntryMap<string, EventsKey> = new SingleEntryMap();

    /** Stores general event listeners on the map. */
    private static readonly eventListenersMap: Map<string, EventsKey> = new Map();

    /** Stores select interaction event listeners. */
    private static readonly selectEventListenersMap: Map<string, Select> = new Map();

    /** Reference to the OpenLayers map instance. */
    private static map: olMap;

    /**
     * Assigns the map instance to the class if not already assigned.
     * @param map - The OpenLayers map instance.
     */
    static setMap(map: olMap) {
        if (this.map != null) {
            return;
        }
        this.map = map;
    }

    /**
     * Retrieves the label of the currently active exclusive event.
     * @returns The label of the active exclusive event, or undefined if none exist.
     */
    static getActiveExclusiveEvent(): string {
        if (this.exclusiveEventListenersMap.size == 0) {
            return;
        }
        return this.exclusiveEventListenersMap.keys().next().value;
    }

    /**
     * Adds an event listener to the map.
     * @param label - Unique label for the event.
     * @param type - The type of event ('singleclick'|'click'|'dblclick'|'pointerdrag'|'pointermove').
     * @param fx - The event handler function.
     * @param isExclusive - Whether the event should be exclusive (default: false).
     */
    static addEventListener(label: string, type: Types, fx: (event: MapBrowserEvent<any>) => unknown, isExclusive = false) {
        if (!this.map) {
            throw new Error("EventListenersOnMap: Map is not assigned");
        }

        if (this.hasEvent(label)) {
            return;
        }

        const eventKey = this.map.on(type, fx);
        isExclusive ? this.exclusiveEventListenersMap.set(label, eventKey) : this.eventListenersMap.set(label, eventKey);
    }

    /**
     * Adds a select interaction event listener to the map.
     * @param label - Unique label for the select event.
     * @param select - The select interaction instance.
     * @param fx - The event handler function for the select event.
     * @param isExclusive - Whether the event should be exclusive (default: false).
     */
    static addSelectEventListener(label: string, select: Select, fx: (event: SelectEvent) => unknown, isExclusive = false) {
        if (!this.map) {
            throw new Error("EventListenersOnMap: Map is not assigned");
        }

        if (this.hasEvent(label)) {
            return;
        }

        const eventKey = select.on('select', fx);
        isExclusive ? this.exclusiveEventListenersMap.set(label, eventKey) : this.eventListenersMap.set(label, eventKey);

        this.map.addInteraction(select);
        this.selectEventListenersMap.set(label, select);
    }

    /**
     * Removes an event listener from the map.
     * @param label - The label of the event to remove.
     * @param isExclusive - Whether the event is exclusive (default: false).
     */
    static RemoveEventListener(label: string, isExclusive = false) {
        const eventMap = isExclusive ? this.exclusiveEventListenersMap : this.eventListenersMap;
        const eventKey = eventMap.get(label);
        if (!eventKey) {
            return;
        }
        unByKey(eventKey);
        eventMap.delete(label);

        const selectInteraction = this.selectEventListenersMap.get(label);
        if (!selectInteraction) {
            return;
        }

        this.map.removeInteraction(selectInteraction);
        this.selectEventListenersMap.delete(label);
    }

    /**
     * Clears all active selections on the map.
     */
    static clearAllSelections() {
        for (const select of this.selectEventListenersMap.values()) {
            const featuresSelectedCollection = select.getFeatures();
            for (let i = featuresSelectedCollection.getLength() - 1; i >= 0; i--) {  // Iterate backward
                const feature = featuresSelectedCollection.item(i);
                featuresSelectedCollection.remove(feature);
            }
        }
    }

    /**
     * Checks if an event exists.
     * @param label - The label of the event to check.
     * @returns True if the event exists, otherwise false.
     */
    static hasEvent(label: string): boolean {
        return this.map && (this.eventListenersMap.has(label) || this.exclusiveEventListenersMap.has(label));
    }
}